"use client"
import { useEffect } from "react"
import { motion } from "framer-motion"
import { ChevronRight, Award, Users, Clock, Shield, Star, ThumbsUp } from "lucide-react"
import Footer from "./Footer"

export default function WhyChooseUsPage() {
  useEffect(()=>{
    window.scrollTo(0,0)
   },[])
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://i.pinimg.com/736x/7d/b9/ca/7db9ca0c2a29940c09e36378696c2092.jpg"
            alt="Company background"
            className="object-cover w-full h-full brightness-50"
          />
        </div>
        <div className="container relative z-10 px-4 mx-auto text-center text-white">
          <motion.div initial="hidden" animate="visible" variants={fadeIn}>
            <h1 className="text-4xl text-white font-bold tracking-tight sm:text-5xl md:text-6xl mb-6">
              Why Choose <span className="text-emerald-400">Our Company</span>
            </h1>
            <p className="max-w-2xl mx-auto text-xl text-gray-200 mb-8">
              We deliver exceptional quality, innovative solutions, and unmatched customer service that sets us apart
              from the competition.
            </p>
           
          </motion.div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container px-4 mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">What Makes Us Different</h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-600">
              At our company, we pride ourselves on delivering excellence in every aspect of our service.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              {
                icon: <Award className="h-10 w-10 text-emerald-500" />,
                title: "Award-Winning Quality",
                description:
                  "Our commitment to excellence has earned us recognition across the industry for our outstanding quality and service.",
              },
              {
                icon: <Users className="h-10 w-10 text-emerald-500" />,
                title: "Expert Team",
                description:
                  "Our team consists of industry veterans with decades of combined experience, ensuring top-tier results for every project.",
              },
              {
                icon: <Clock className="h-10 w-10 text-emerald-500" />,
                title: "Timely Delivery",
                description:
                  "We understand the importance of deadlines and pride ourselves on delivering projects on time, every time.",
              },
              {
                icon: <Shield className="h-10 w-10 text-emerald-500" />,
                title: "Guaranteed Satisfaction",
                description:
                  "Your satisfaction is our priority. We work tirelessly until you're completely happy with the results.",
              },
              {
                icon: <ThumbsUp className="h-10 w-10 text-emerald-500" />,
                title: "Innovative Solutions",
                description:
                  "We stay ahead of industry trends to provide cutting-edge solutions that give you a competitive advantage.",
              },
              {
                icon: <Star className="h-10 w-10 text-emerald-500" />,
                title: "Personalized Approach",
                description:
                  "We tailor our services to your specific needs, ensuring solutions that align perfectly with your goals.",
              },
            ].map((benefit, index) => (
              <motion.div key={index} variants={fadeIn}>
                <div className="h-full bg-white rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-300 p-6 flex flex-col items-center text-center">
                  <div className="mb-4 p-3 rounded-full bg-emerald-50">{benefit.icon}</div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container px-4 mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">What Our Clients Say</h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-600">
              Don't just take our word for it. Here's what our clients have to say about working with us.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              {
                quote:
                  "Working with this company transformed our business. Their innovative approach and attention to detail exceeded our expectations.",
                author: "Mansoor Rasoli",
                position: "CEO, SPARK TRUST",
              },
              {
                quote:
                  "The team consistently delivers exceptional results. Their expertise and dedication make them a valuable partner.",
                author: "Daryosh Sharifie",
                position: "",
              },
              {
                quote:
                  "Their commitment to quality and customer satisfaction is unmatched. They've been instrumental in our growth.",
                author: "Emily Rodriguez",
                position: "Operations Manager, GrowthWave",
              },
            ].map((testimonial, index) => (
              <motion.div key={index} variants={fadeIn}>
                <div className="h-full bg-white rounded-lg shadow-sm hover:shadow-lg transition-shadow duration-300 p-6">
                  <div className="mb-4 text-emerald-500">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="inline-block h-5 w-5 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-bold">{testimonial.author}</p>
                    <p className="text-sm text-gray-500">{testimonial.position}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-900 text-white">
        <div className="container px-4 mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-6">Ready to Experience the Difference?</h2>
            <p className="text-xl text-emerald-100 mb-8">
              Join our growing list of satisfied clients and discover why we are the partner of choice for businesses
              that demand excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
     <Footer/>
    </div>
  )
}

